<script setup lang="ts">
  // One caja movement of the V2 movements list. Presentational: the parent
  // resolves every label and owns the actions. Two layouts by the `wide` prop:
  // narrow = two-line row whose tap opens the actions dialog (chevron slot
  // always reserved so amounts stay in one column); wide = one line with the
  // labeled action buttons inline.
  import { computed } from 'vue';
  import {
    ICajaMovementRow,
    TCajaMovementAction,
  } from '@/domain/caja/caja-movement-row';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  export interface ICajaMovementActionView {
    action: TCajaMovementAction;
    label: string;
    icon: string;
    color: 'neutral' | 'error';
    variant: 'outline' | 'soft';
  }

  export interface ICajaMovementRowView extends ICajaMovementRow {
    typeLabel: string;
    /** Null when the movement carries no payment method (open/close). */
    methodLabel: string | null;
    timeText: string;
    /** Signed, formatted amount ("+$ 110"). */
    amountText: string;
    /** "18:48 · Federico · 1 pedido" (narrow tier). */
    metaText: string;
    actionViews: ICajaMovementActionView[];
  }

  const props = defineProps<{
    view: ICajaMovementRowView;
    wide: boolean;
    /** An action is running somewhere in the list: buttons disabled. */
    busy: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'action', action: TCajaMovementAction): void;
  }>();

  const tappable = computed(
    () => !props.wide && props.view.actionViews.length > 0
  );

  function onRowActivate(): void {
    if (tappable.value) emit('open');
  }
</script>

<template>
  <div
    class="v2-caja-movement-row"
    :class="[
      `tone-${view.tone}`,
      { 'is-wide': wide, 'is-tappable': tappable },
    ]"
    :role="tappable ? 'button' : undefined"
    :tabindex="tappable ? 0 : undefined"
    @click="onRowActivate"
    @keydown.enter.self.prevent="onRowActivate"
    @keydown.space.self.prevent="onRowActivate"
  >
    <span v-if="wide" class="row-time">{{ view.timeText }}</span>

    <span class="row-tile">
      <PosUiIcon :name="view.icon" />
    </span>

    <span class="row-main">
      <span class="row-title">
        <span class="row-type">{{ view.typeLabel }}</span>
        <template v-if="wide && view.userName">
          <span class="row-dot">·</span>
          <span class="row-seg">{{ view.userName }}</span>
        </template>
        <template v-if="view.methodLabel">
          <span class="row-dot">·</span>
          <span class="row-method">{{ view.methodLabel }}</span>
        </template>
      </span>
      <span v-if="!wide" class="row-meta" :title="view.metaText">
        {{ view.metaText }}
      </span>
      <!-- Own line in both tiers: inline, the note got ~60-110px on a 1024x600
           tablet and server notes ("Reembolso (Mesa 1)") never fit. -->
      <span v-if="view.description" class="row-desc" :title="view.description">
        {{ view.description }}
      </span>
    </span>

    <span class="row-end">
      <span class="row-amount">{{ view.amountText }}</span>
      <!-- No icon here: on a 360px phone the details column is ~100px and
           every badge pixel comes out of it. -->
      <PosUiBadge
        v-if="view.reimbursed && !wide"
        color="neutral"
        variant="soft"
        size="sm"
        class="row-badge"
      >
        {{ $t('posV2.payReimbursed') }}
      </PosUiBadge>
    </span>

    <!-- Narrow: the slot is always rendered so amounts share one column. -->
    <span v-if="!wide" class="row-chevron">
      <PosUiIcon v-if="tappable" name="chevron-right" />
    </span>

    <!-- Wide: fixed-min-width column so amounts share one column across rows.
         Label-only buttons (the icons live in the actions dialog): the three
         Spanish labels measure ~406px with icons off, ~485px with them on. -->
    <span v-else class="row-actions">
      <PosUiBadge
        v-if="view.reimbursed"
        color="neutral"
        variant="soft"
        size="sm"
        icon="cash-refund"
        class="row-badge"
      >
        {{ $t('posV2.payReimbursed') }}
      </PosUiBadge>
      <PosUiButton
        v-for="a in view.actionViews"
        :key="a.action"
        :color="a.color"
        :variant="a.variant"
        size="md"
        :disabled="busy"
        class="row-action"
        @click="emit('action', a.action)"
      >
        {{ a.label }}
      </PosUiButton>
    </span>
  </div>
</template>

<style scoped lang="scss">
  .v2-caja-movement-row {
    @apply tw-flex tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-border-l-4 tw-bg-white tw-px-3 tw-py-2;

    & + & {
      @apply tw-mt-1;
    }

    &.is-tappable {
      @apply tw-cursor-pointer tw-select-none;

      &:active {
        @apply tw-brightness-95;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    // Tinted skin: full-row tint + stronger left accent, tile one shade
    // stronger than the row so the icon still reads as a chip.
    &.tone-in {
      @apply tw-border-l-emerald-500 tw-bg-emerald-50;

      .row-tile {
        @apply tw-bg-emerald-100 tw-text-emerald-700;
      }

      .row-end .row-amount {
        @apply tw-text-emerald-700;
      }
    }

    &.tone-out {
      @apply tw-border-l-red-500 tw-bg-red-50;

      .row-tile {
        @apply tw-bg-red-100 tw-text-red-700;
      }

      .row-end .row-amount {
        @apply tw-text-red-700;
      }
    }

    &.tone-neutral {
      @apply tw-border-l-neutral-300;

      .row-tile {
        @apply tw-bg-neutral-200 tw-text-neutral-700;
      }
    }

    // Struck, not faded: audit rows must stay legible.
    &.tone-refunded {
      @apply tw-border-l-neutral-300 tw-bg-neutral-50;

      .row-tile {
        @apply tw-bg-neutral-200 tw-text-neutral-500;
      }

      .row-main .row-title .row-type,
      .row-end .row-amount {
        @apply tw-line-through tw-text-neutral-400;
      }
    }

    // Fixed so tiles line up when a session mixes "HH:mm" (today) and
    // "d/M HH:mm" (before midnight) rows; "31/12 23:59" measures ~65px.
    .row-time {
      @apply tw-w-[68px] tw-flex-none tw-whitespace-nowrap tw-text-xs tw-tabular-nums tw-text-neutral-500;
    }

    .row-tile {
      @apply tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-md tw-text-lg;
    }

    .row-main {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

      .row-title {
        @apply tw-flex tw-min-w-0 tw-items-baseline tw-gap-1 tw-text-sm tw-leading-5;

        .row-type {
          @apply tw-flex-none tw-font-semibold tw-text-neutral-900;
        }

        .row-dot {
          @apply tw-flex-none tw-text-neutral-400;
        }

        // The user never truncates; the method gives way first: a row on a
        // 360px phone has ~100px for this line.
        .row-seg {
          @apply tw-flex-none tw-text-neutral-700;
        }

        .row-method {
          @apply tw-min-w-0 tw-truncate tw-text-neutral-700;
        }
      }

      .row-meta {
        @apply tw-truncate tw-text-xs tw-leading-4 tw-tabular-nums tw-text-neutral-500;
      }

      .row-desc {
        @apply tw-mt-0.5 tw-text-xs tw-leading-4 tw-text-neutral-600;
      }
    }

    // Clamped only where the rest is reachable (the actions dialog shows the
    // full note); every other row shows it whole, as nothing else could.
    // Notes are at most 255 chars (140 when typed in the POS), so an unclamped
    // one is a few lines at most.
    &.is-tappable .row-main .row-desc {
      @apply tw-line-clamp-2;
    }

    // Amounts are right-aligned, so they line up by their right edge without
    // a reserved width; the narrow tier needs every pixel for the details.
    .row-end {
      @apply tw-flex tw-flex-none tw-flex-col tw-items-end tw-gap-0.5;

      .row-amount {
        @apply tw-whitespace-nowrap tw-text-sm tw-font-bold tw-tabular-nums tw-text-neutral-900;
      }
    }

    .row-chevron {
      @apply tw-flex tw-w-5 tw-flex-none tw-items-center tw-justify-center tw-text-lg tw-text-neutral-400;
    }

    // Minimum, not fixed: a longer locale grows the column instead of
    // overflowing into the amount; Spanish (406px measured) stays aligned.
    // Every extra pixel here is taken from the details column at 1024x600.
    // min-h reserves the md button height so buttonless rows match the rest.
    .row-actions {
      @apply tw-flex tw-min-h-11 tw-min-w-[416px] tw-flex-none tw-items-center tw-justify-end tw-gap-2;
    }

    .row-badge {
      @apply tw-flex-none;
    }

    &.is-wide {
      @apply tw-gap-3 tw-py-1.5;

      // Reserved width keeps the details column from crowding the amount.
      .row-end {
        @apply tw-min-w-[84px] tw-flex-row tw-items-center tw-justify-end;
      }
    }
  }
</style>
